import {Component, Inject} from '@angular/core';
import {ProjectId} from '@webmate-web-core/shared/types/ids';
import {mergeMap} from 'rxjs/operators';
import {ModalToolContainerService, WebmateToolConfig} from '../modaltoolcontainer/modaltoolcontainer.service';
import {TOOL_CONTAINER_DATA} from '../modaltoolcontainer/modaltoolcontainer.component';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {getFileExtension} from '@webmate-web-core/shared/utils/file';
import {PackageService} from '@webmate-web-core/shared/services/package.service';
import {AssetAndBlobService} from '@webmate-web-core/shared/services/assetAndBlob.service';
import {WmNotificationService} from '@webmate-web-core/shared/components/notification/wm-notification.service';
import {VirtualPackage} from '@webmate-web-core/shared/types/packages/package';

type AppUploadStep = 'select-package-source' | 'upload-package' | 'connect-package-repo';

export class AppUploadToolConfig implements WebmateToolConfig {
    toolName = 'Configure App Source';
    scrollable = false;
    noFixedHeight = true;

    constructor(public projectId: ProjectId,
                public initialStep: AppUploadStep = 'select-package-source',
                public updatePackage?: VirtualPackage,
                public onPackageCreated?: (pkg: VirtualPackage) => void) {
    }
}

@Component({
    templateUrl: './app-upload-tool.component.html',
    styleUrls: ['./app-upload-tool.component.less']
})
export class AppUploadToolComponent {

    readonly acceptedAppFileExtensions = ['.apk', '.ipa'];

    activeStep: AppUploadStep;

    loadedFile?: { file: File, url: any };

    uiInProgress = false;
    testInProgress = false;

    latestPackageSourceConfigTestResult?: string;
    latestPackageSourceConfigTestResultSuccessful = false;

    pkgSrcConfigForm?: UntypedFormGroup;

    constructor(@Inject(TOOL_CONTAINER_DATA) private data: AppUploadToolConfig, private toolService: ModalToolContainerService,
                private formBuilder: UntypedFormBuilder, private packageService: PackageService,
                private blobService: AssetAndBlobService, private notificationService: WmNotificationService) {
        this.activeStep = this.data.initialStep;
    }

    upload(event: any) {
        if (!this.loadedFile) {
            return;
        }
        this.uiInProgress = true;
        let extension = getFileExtension(this.loadedFile.file.name);
        if (this.acceptedAppFileExtensions.includes(`.${extension}`)) {
            const isUpdate = !!this.data.updatePackage;
            // Seed the package name with the file basename so users don't see
            // "Unnamed app" while backend metadata extraction is still pending.
            const initialName = this.loadedFile.file.name.replace(/\.[^.]+$/, '');
            this.blobService.putBlob(this.loadedFile.file, this.data.projectId).pipe(mergeMap(bid => {
                if (this.data.updatePackage) {
                    return this.packageService.updatePackage(this.data.updatePackage.id, bid, this.data.updatePackage.name, extension, this.data.projectId);
                } else {
                    return this.packageService.createPackage(this.data.projectId, bid, initialName, extension);
                }
            })).subscribe({
                next: (pkg: VirtualPackage) => {
                    if (!isUpdate) {
                        this.data.onPackageCreated?.(pkg);
                    }
                    this.toolService.closeLastTool();
                },
                error: () => {
                    this.toolService.closeLastTool();
                    this.notificationService.toastErrorNotification('An error occurred while uploading the app. Please try again.');
                }
            });
        } else {
            this.notificationService.toastErrorNotification(`Only apps with file extension ${this.acceptedAppFileExtensions.join(', ')} are allowed.`);
        }
        event.stopPropagation();
        event.preventDefault();
    }

    initializePackageRepoForm(repositoryType: string) {
        if (repositoryType === 'appcenter') {
            this.pkgSrcConfigForm = this.formBuilder.group({
                'repositoryType': ['appcenter'],
                'apiKey': ['', Validators.required],
                'owner': ['', Validators.required],
                'appName': ['', Validators.required],
                'branch': ['master', Validators.required],
                'wantsBuildArtifact': [true]
            });
        }

        this.pkgSrcConfigForm!.valueChanges.subscribe(value => {
            if (this.pkgSrcConfigForm?.get('wantsBuildArtifact')?.value && !this.pkgSrcConfigForm?.contains('branch')) {
                this.pkgSrcConfigForm!.addControl('branch', this.formBuilder.control('master'));
            }
        });
    }

    selectFile(event: Event) {
        const target = event.target as HTMLInputElement;
        const files = target.files as FileList;
        Array.from(files).forEach((f: File) => this.addFile(f));
    }

    dropFiles(event: File[]) {
        event.forEach((f: File) => {
            this.addFile(f);
        });
    }

    addFile(f: File) {
        let extension = getFileExtension(f.name);
        if (this.acceptedAppFileExtensions.includes(`.${extension}`)) {
            const reader = new FileReader();
            reader.onload = (e) => {
                this.loadedFile = {
                    url: reader.result,
                    file: f
                };
            };
            reader.readAsDataURL(f);
        } else {
            this.notificationService.toastErrorNotification(`Only apps with file extension ${this.acceptedAppFileExtensions.join(', ')} are allowed.`);
        }
    }

    makePackageSourceConfigFromForm(form: UntypedFormGroup): any {
        return {
            type: 'AppCenterPackageSourceConfig',
            appName: form.get('appName')?.value,
            owner: form.get('owner')?.value,
            branch: form.get('branch')?.value,
            apiKey: form.get('apiKey')?.value,
            wantBuild: form.get('wantsBuildArtifact')?.value
        };
    }

    testPackageSourceConfig(event: any) {
        if (this.pkgSrcConfigForm?.invalid) {
            return;
        }
        this.testInProgress = true;
        let config = this.makePackageSourceConfigFromForm(this.pkgSrcConfigForm!);
        event.stopPropagation();
        event.preventDefault();

        this.packageService.testPackageSourceConfig(config).subscribe(result => {
            this.latestPackageSourceConfigTestResult = result['error'] || 'Connection successful';
            this.latestPackageSourceConfigTestResultSuccessful = !result['error'];
            setTimeout(() => this.latestPackageSourceConfigTestResult = undefined, 3000);
            this.testInProgress = false;
        });
    }

    savePackageWithPackageSourceConfig(event: any) {
        if (this.pkgSrcConfigForm?.invalid) {
            return;
        }
        event.stopPropagation();
        event.preventDefault();
        this.uiInProgress = true;

        let config = this.makePackageSourceConfigFromForm(this.pkgSrcConfigForm!);
        this.packageService.createPackageFromPackageSource(this.data.projectId, config, '').subscribe((pkg: VirtualPackage) => {
            this.uiInProgress = false;
            this.data.onPackageCreated?.(pkg);
            this.toolService.closeLastTool();
        });

    }

    cancelWizard() {
        this.toolService.closeLastTool();
    }

    openStep(step: AppUploadStep) {
        this.activeStep = step;
    }

}
